import { useMemo } from 'react';
import { skipToken } from '@reduxjs/toolkit/query';
import { useGetPatientStudiesQuery } from '@/state/slices/studies';
import type { ApiStudyWithReports, PatientStudiesParams } from '@/models/study';
import type { PriorStudyVM } from '../types';
import { formatModalityShort } from '../utils/formatPriorStudy';

interface UsePriorStudiesArgs {
  patientUuid: string | undefined;
  currentStudyUuid: string | undefined;
}

function latestReportUuid(study: ApiStudyWithReports): string | null {
  if (!study.reports?.length) return null;
  const sorted = [...study.reports].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  return sorted[0]?.uuid ?? null;
}

function toVM(study: ApiStudyWithReports): PriorStudyVM {
  return {
    uuid: study.uuid,
    typeLabel: formatModalityShort(study.modality),
    dateISO: study.studyDate,
    reportUuid: latestReportUuid(study),
  };
}

export function usePriorStudies({
  patientUuid,
  currentStudyUuid,
}: UsePriorStudiesArgs) {
  const queryArg: PatientStudiesParams | typeof skipToken = patientUuid
    ? {
        patient_uuid: patientUuid,
        include: 'reports',
        paginate: false,
        ordering: '-study_date',
      }
    : skipToken;

  const { data, isLoading, isError } = useGetPatientStudiesQuery(queryArg);

  const priors = useMemo<PriorStudyVM[]>(() => {
    if (!data?.length) return [];
    return data.filter((s) => s.uuid !== currentStudyUuid).map(toVM);
  }, [data, currentStudyUuid]);

  return { priors, isLoading, isError };
}
